import {Fragment, useMemo} from "react";
import {css} from "@panda/css";
import {isFuture, parseISO} from "date-fns";

import {Accordion} from "@pg-design/accordion-module";
import {mb, pv} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";
import {dateTimeFormat, formatDate, numberFormat, SUP2} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {rpFeatureFlagPlanner2dPropertyAttributes, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {ResponsiveTable} from "../../atoms/ResponsiveTable";
import {ResponsiveTableRow} from "../../atoms/ResponsiveTableRow";
import {OfferDetailFacilities} from "../../offer/detail/components/OfferDetailFacilities";
import {OfferDetailNaturalSites} from "../../offer/detail/components/OfferNaturalSites";
import {defaultStandardDescription, OfferStandardDescription} from "../../offer/detail/components/OfferStandardDescription";
import {OfferType} from "../../offer/types/OfferType";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {VENDOR_REPORT_SECTION_ID} from "../constants/vendor_report_section";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {isPropertyForSale} from "../utils/is_property_for_sale";
import {PropertyDetailAdditionalSpaces} from "./property_additional_spaces/PropertyDetailAdditionalSpaces";
import {PropertyLocationAttributes} from "./property_location_attributes/PropertyLocationAttributes";
import {PropertyDetailDetailsParkingInquiryButton} from "./PropertyDetailDetailsParkingInquiryButton";
import {PropertyDetailEnergyPerformance} from "./PropertyDetailEnergyPerformance";
import {PropertyDetailHeaderPrice} from "./PropertyDetailHeaderPrice";
import {PropertyDetailPictograms} from "./PropertyDetailPictograms";
import {PropertyDetailPriceHistory} from "./PropertyDetailPriceHistory";
import {PropertyDetailVendorReportSection} from "./PropertyDetailVendorReportSection";

export interface IProps {
    hasPropertyShownPrice?: boolean;
    property: IPropertyDetail;
    variables: {
        property_room_area_type: IValueLabel[];
        property_additional_areas: IValueLabel[];
        property_kitchen_type: IValueLabel[];
        property_quarters: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        property_ownership_form: IValueLabel[];
        offer_funds_protection: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_natural_sites: IValueLabel[];
    } | null;
}

const getGarageStandString = (number: number) => {
    switch (number) {
        case 1:
            return "jednostanowiskowy";
        case 2:
            return "dwustanowiskowy";
        default:
            return `${number}-stanowiskowy`;
    }
};

const INFO_FROM_VENDOR = "informacja u dewelopera";

export const PropertyDetailDetails = (props: IProps) => {
    const {property, variables} = props;
    const isAbroad = property.offer.region.country !== Country.POLAND;

    const {isMobile} = useUserDevice();
    const isPriceButtonAvailable = isPropertyForSale(property);

    const ownershipFormTypes = variables && variables.property_ownership_form;
    const foundOwnershipForm = ownershipFormTypes?.find((aAType) => aAType.value === property.ownership_form);

    const foundFundProtection = variables && variables.offer_funds_protection;
    const foundFundProtectionTypes = foundFundProtection?.find((fPT) => fPT.value === property.offer.funds_protection);

    const rentPrice = useMemo(() => {
        return property.offer.rent_price_m2_range && (property.offer.rent_price_m2_range.lower || property.offer.rent_price_m2_range.upper)
            ? `planowany czynsz będzie w wysokości ${property.offer.rent_price_m2_range.lower && `od ${property.offer.rent_price_m2_range.lower}`} ${
                  property.offer.rent_price_m2_range.upper ? `do ${property.offer.rent_price_m2_range.upper}` : ""
              } zł/m${SUP2} ${
                  property.rent_price_range.lower || property.rent_price_range.upper
                      ? `(czyli ${property.rent_price_range.lower ? `od ${numberFormat(property.rent_price_range.lower)}` : ""} ${
                            property.rent_price_range.upper ? `do ${numberFormat(property.rent_price_range.upper)}` : ""
                        } zł za całość)`
                      : ""
              }`
            : `${
                  property.offer.rent_price_m2_range && property.offer.rent_price_m2_range.lower === 0 && property.offer.rent_price_m2_range.upper === 0
                      ? "nieruchomość bezczynszowa"
                      : INFO_FROM_VENDOR
              }`;
    }, [property]);

    const standardDescription = useMemo(() => {
        const hasDescription = () => Object.values(property.offer.standard_description || {})?.filter((field) => isEmpty(field)).length > 0;

        return property.is_turnkey || (property.offer.standard_description && hasDescription()) ? (
            <OfferStandardDescription
                types={variables != null ? variables.offer_standard_description_type : []}
                standardDescription={property.offer.standard_description || defaultStandardDescription}
                showOnlyTurnkeyStandard={property.is_turnkey}
            />
        ) : (
            <ResponsiveTableRow title="Standard wykończenia:">{INFO_FROM_VENDOR}</ResponsiveTableRow>
        );
    }, [property]);

    const constructionEndDateText = useMemo(() => {
        return property.offer.construction_date_range.upper || property.construction_end_date
            ? property.construction_end_date
                ? isFuture(parseISO(property.construction_end_date))
                    ? `oddanie do użytkowania w ${formatDate(property.construction_end_date, dateTimeFormat.quarter)} roku`
                    : `została oddana do użytkowania w ${formatDate(property.construction_end_date, dateTimeFormat.quarter)} roku`
                : isFuture(parseISO(property.offer.construction_date_range.upper))
                  ? `${
                        property.offer.construction_date_range.lower &&
                        `rozpoczęcie budowy w ${formatDate(
                            property.offer.construction_date_range.lower,
                            dateTimeFormat.quarter
                        )} roku, planowane oddanie do użytkowania w ${formatDate(property.offer.construction_date_range.upper, dateTimeFormat.quarter)} roku`
                    }`
                  : `${
                        property.offer.construction_date_range.lower &&
                        `rozpoczęcie budowy w ${formatDate(
                            property.offer.construction_date_range.lower,
                            dateTimeFormat.quarter
                        )} roku, inwestycja została oddana do użytkowania w ${formatDate(
                            property.offer.construction_date_range.upper,
                            dateTimeFormat.quarter
                        )} roku`
                    }`
            : INFO_FROM_VENDOR;
    }, [property]);

    const offerFacilities = useMemo(() => {
        return property.offer.facilities && property.offer.facilities.length ? (
            <OfferDetailFacilities facilities={property.offer.facilities} facilitiesTypes={variables != null ? variables.offer_facilities : []} />
        ) : (
            INFO_FROM_VENDOR
        );
    }, [property]);

    const offerNaturalSites = useMemo(() => {
        return property.offer.natural_sites && property.offer.natural_sites.length ? (
            <OfferDetailNaturalSites naturalSites={property.offer.natural_sites} naturalSitesTypes={variables != null ? variables.offer_natural_sites : []} />
        ) : (
            INFO_FROM_VENDOR
        );
    }, [property]);

    const vendorId = property.offer.vendor.id;

    return (
        <>
            <PropertyDetailPictograms {...props} />

            {rpFeatureFlagPlanner2dPropertyAttributes ? <PropertyLocationAttributes css={mb(2)} /> : null}

            <section>
                <PropertyDetailPriceHistory property={property} />
            </section>

            {rpFeatureFlagVendorReportDownload && !isAbroad && (
                <section id={VENDOR_REPORT_SECTION_ID}>
                    <PropertyDetailVendorReportSection vendorId={vendorId} />
                </section>
            )}

            <div className={accordions}>
                {props.hasPropertyShownPrice && variables?.property_additional_areas && property.additional_areas_for_configurator.length > 0 && (
                    <Accordion>
                        <Accordion.Header>
                            <Text as="h3" variant="headline_6">
                                Zakup możliwy jest w pakiecie zawierającym poniższe produkty
                            </Text>
                        </Accordion.Header>
                        <Accordion.Content>
                            <PropertyDetailAdditionalSpaces
                                additionalSpaces={property.additional_areas_for_configurator}
                                variables={variables}
                                property={property}
                            />
                            {!isMobile && isPriceButtonAvailable && (
                                <div css={pv(1)}>
                                    <PropertyDetailHeaderPrice property={property} />
                                </div>
                            )}
                        </Accordion.Content>
                    </Accordion>
                )}

                <Accordion>
                    <Accordion.Header>
                        <Text as="h3" variant="headline_6">
                            Budynek i otoczenie
                        </Text>
                    </Accordion.Header>
                    <Accordion.Content>
                        <ResponsiveTable>
                            <ResponsiveTableRow
                                title="Miejsca postojowe"
                                text={
                                    <PropertyDetailDetailsParkingInquiryButton
                                        vendorId={property.offer.vendor.id}
                                        offerId={property.offer.id}
                                        propertyId={property.id}
                                        vendorName={property.offer.vendor.name}
                                        offerName={property.offer.name}
                                    />
                                }
                            />
                            {!!property.garages && <ResponsiveTableRow title="Garaże" text={`garaż ${getGarageStandString(property.garages)}`} />}
                            {property.offer.type === OfferType.FLAT && property.offer.storage ? (
                                <ResponsiveTableRow
                                    title="Komórki lokatorskie"
                                    text={
                                        <Fragment>
                                            dostępne{property.offer.storage.quantity ? ` (${property.offer.storage.quantity})` : ""}
                                            {property.offer.storage.description ? `: ${property.offer.storage.description}` : ""}
                                        </Fragment>
                                    }
                                />
                            ) : null}
                            {standardDescription}
                            <ResponsiveTableRow title="Realizacja inwestycji" text={constructionEndDateText} />
                            <ResponsiveTableRow title="Udogodnienia" text={offerFacilities} />
                            <ResponsiveTableRow title="Przyroda" text={offerNaturalSites} />
                            <ResponsiveTableRow title="Charakterystyka energetyczna nieruchomości">
                                <PropertyDetailEnergyPerformance property={property} />
                            </ResponsiveTableRow>
                        </ResponsiveTable>
                    </Accordion.Content>
                </Accordion>

                <Accordion>
                    <Accordion.Header>
                        <Text as="h3" variant="headline_6">
                            Rezerwacja i finanse
                        </Text>
                    </Accordion.Header>
                    <Accordion.Content>
                        <ResponsiveTable>
                            <ResponsiveTableRow title="Rodzaj własności" text={foundOwnershipForm ? `${foundOwnershipForm.label}` : INFO_FROM_VENDOR} />
                            <ResponsiveTableRow title="Wysokość czynszu" text={rentPrice} />
                            <ResponsiveTableRow
                                title="Warunki rezerwacji"
                                text={property.offer.reservation_description ? property.offer.reservation_description : INFO_FROM_VENDOR}
                            />
                            <ResponsiveTableRow
                                title="Zabezpieczenie środków"
                                text={foundFundProtectionTypes ? `${foundFundProtectionTypes.label}` : INFO_FROM_VENDOR}
                            />
                        </ResponsiveTable>
                    </Accordion.Content>
                </Accordion>
            </div>
        </>
    );
};

const accordions = css({
    display: "flex",
    flexDirection: "column",
    rowGap: "1.6rem"
});
import {Fragment, useMemo} from "react";
import {css} from "@panda/css";
import {isFuture, parseISO} from "date-fns";

import {Accordion} from "@pg-design/accordion-module";
import {mb, pv} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {isEmpty} from "@pg-mono/nodash";
import {dateTimeFormat, formatDate, numberFormat, SUP2} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";
import {Country} from "@pg-rp/common-types";

import {rpFeatureFlagPlanner2dPropertyAttributes, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {ResponsiveTable} from "../../atoms/ResponsiveTable";
import {ResponsiveTableRow} from "../../atoms/ResponsiveTableRow";
import {OfferDetailFacilities} from "../../offer/detail/components/OfferDetailFacilities";
import {OfferDetailNaturalSites} from "../../offer/detail/components/OfferNaturalSites";
import {defaultStandardDescription, OfferStandardDescription} from "../../offer/detail/components/OfferStandardDescription";
import {OfferType} from "../../offer/types/OfferType";
import {IValueLabel} from "../../variables/types/IValueLabel";
import {VENDOR_REPORT_SECTION_ID} from "../constants/vendor_report_section";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {isPropertyForSale} from "../utils/is_property_for_sale";
import {PropertyDetailAdditionalSpaces} from "./property_additional_spaces/PropertyDetailAdditionalSpaces";
import {PropertyLocationAttributes} from "./property_location_attributes/PropertyLocationAttributes";
import {PropertyDetailDetailsParkingInquiryButton} from "./PropertyDetailDetailsParkingInquiryButton";
import {PropertyDetailEnergyPerformance} from "./PropertyDetailEnergyPerformance";
import {PropertyDetailHeaderPrice} from "./PropertyDetailHeaderPrice";
import {PropertyDetailPictograms} from "./PropertyDetailPictograms";
import {PropertyDetailPriceHistory} from "./PropertyDetailPriceHistory";
import {PropertyDetailVendorReportSection} from "./PropertyDetailVendorReportSection";

export interface IProps {
    hasPropertyShownPrice?: boolean;
    property: IPropertyDetail;
    variables: {
        property_room_area_type: IValueLabel[];
        property_additional_areas: IValueLabel[];
        property_kitchen_type: IValueLabel[];
        property_quarters: IValueLabel[];
        offer_standard_description_type: IValueLabel[];
        property_ownership_form: IValueLabel[];
        offer_funds_protection: IValueLabel[];
        offer_facilities: IValueLabel[];
        offer_natural_sites: IValueLabel[];
    } | null;
}

const getGarageStandString = (number: number) => {
    switch (number) {
        case 1:
            return "jednostanowiskowy";
        case 2:
            return "dwustanowiskowy";
        default:
            return `${number}-stanowiskowy`;
    }
};

const INFO_FROM_VENDOR = "informacja u dewelopera";

export const PropertyDetailDetails = (props: IProps) => {
    const {property, variables} = props;
    const isAbroad = property.offer.region.country !== Country.POLAND;

    const {isMobile} = useUserDevice();
    const isPriceButtonAvailable = isPropertyForSale(property);

    const ownershipFormTypes = variables && variables.property_ownership_form;
    const foundOwnershipForm = ownershipFormTypes?.find((aAType) => aAType.value === property.ownership_form);

    const foundFundProtection = variables && variables.offer_funds_protection;
    const foundFundProtectionTypes = foundFundProtection?.find((fPT) => fPT.value === property.offer.funds_protection);

    const rentPrice = useMemo(() => {
        return property.offer.rent_price_m2_range && (property.offer.rent_price_m2_range.lower || property.offer.rent_price_m2_range.upper)
            ? `planowany czynsz będzie w wysokości ${property.offer.rent_price_m2_range.lower && `od ${property.offer.rent_price_m2_range.lower}`} ${
                  property.offer.rent_price_m2_range.upper ? `do ${property.offer.rent_price_m2_range.upper}` : ""
              } zł/m${SUP2} ${
                  property.rent_price_range.lower || property.rent_price_range.upper
                      ? `(czyli ${property.rent_price_range.lower ? `od ${numberFormat(property.rent_price_range.lower)}` : ""} ${
                            property.rent_price_range.upper ? `do ${numberFormat(property.rent_price_range.upper)}` : ""
                        } zł za całość)`
                      : ""
              }`
            : `${
                  property.offer.rent_price_m2_range && property.offer.rent_price_m2_range.lower === 0 && property.offer.rent_price_m2_range.upper === 0
                      ? "nieruchomość bezczynszowa"
                      : INFO_FROM_VENDOR
              }`;
    }, [property]);

    const standardDescription = useMemo(() => {
        const hasDescription = () => Object.values(property.offer.standard_description || {})?.filter((field) => isEmpty(field)).length > 0;

        return property.is_turnkey || (property.offer.standard_description && hasDescription()) ? (
            <OfferStandardDescription
                types={variables != null ? variables.offer_standard_description_type : []}
                standardDescription={property.offer.standard_description || defaultStandardDescription}
                showOnlyTurnkeyStandard={property.is_turnkey}
            />
        ) : (
            <ResponsiveTableRow title="Standard wykończenia:">{INFO_FROM_VENDOR}</ResponsiveTableRow>
        );
    }, [property]);

    const constructionEndDateText = useMemo(() => {
        return property.offer.construction_date_range.upper || property.construction_end_date
            ? property.construction_end_date
                ? isFuture(parseISO(property.construction_end_date))
                    ? `oddanie do użytkowania w ${formatDate(property.construction_end_date, dateTimeFormat.quarter)} roku`
                    : `została oddana do użytkowania w ${formatDate(property.construction_end_date, dateTimeFormat.quarter)} roku`
                : isFuture(parseISO(property.offer.construction_date_range.upper))
                  ? `${
                        property.offer.construction_date_range.lower &&
                        `rozpoczęcie budowy w ${formatDate(
                            property.offer.construction_date_range.lower,
                            dateTimeFormat.quarter
                        )} roku, planowane oddanie do użytkowania w ${formatDate(property.offer.construction_date_range.upper, dateTimeFormat.quarter)} roku`
                    }`
                  : `${
                        property.offer.construction_date_range.lower &&
                        `rozpoczęcie budowy w ${formatDate(
                            property.offer.construction_date_range.lower,
                            dateTimeFormat.quarter
                        )} roku, inwestycja została oddana do użytkowania w ${formatDate(
                            property.offer.construction_date_range.upper,
                            dateTimeFormat.quarter
                        )} roku`
                    }`
            : INFO_FROM_VENDOR;
    }, [property]);

    const offerFacilities = useMemo(() => {
        return property.offer.facilities && property.offer.facilities.length ? (
            <OfferDetailFacilities facilities={property.offer.facilities} facilitiesTypes={variables != null ? variables.offer_facilities : []} />
        ) : (
            INFO_FROM_VENDOR
        );
    }, [property]);

    const offerNaturalSites = useMemo(() => {
        return property.offer.natural_sites && property.offer.natural_sites.length ? (
            <OfferDetailNaturalSites naturalSites={property.offer.natural_sites} naturalSitesTypes={variables != null ? variables.offer_natural_sites : []} />
        ) : (
            INFO_FROM_VENDOR
        );
    }, [property]);

    const vendorId = property.offer.vendor.id;

    return (
        <>
            <PropertyDetailPictograms {...props} />

            {rpFeatureFlagPlanner2dPropertyAttributes ? <PropertyLocationAttributes css={mb(2)} /> : null}

            <section>
                <PropertyDetailPriceHistory property={property} />
            </section>

            {rpFeatureFlagVendorReportDownload && !isAbroad && (
                <section id={VENDOR_REPORT_SECTION_ID}>
                    <PropertyDetailVendorReportSection vendorId={vendorId} />
                </section>
            )}

            <div className={accordions}>
                {props.hasPropertyShownPrice && variables?.property_additional_areas && property.additional_areas_for_configurator.length > 0 && (
                    <Accordion>
                        <Accordion.Header>
                            <Text as="h3" variant="headline_6">
                                Zakup możliwy jest w pakiecie zawierającym poniższe produkty
                            </Text>
                        </Accordion.Header>
                        <Accordion.Content>
                            <PropertyDetailAdditionalSpaces
                                additionalSpaces={property.additional_areas_for_configurator}
                                variables={variables}
                                property={property}
                            />
                            {!isMobile && isPriceButtonAvailable && (
                                <div css={pv(1)}>
                                    <PropertyDetailHeaderPrice property={property} />
                                </div>
                            )}
                        </Accordion.Content>
                    </Accordion>
                )}

                <Accordion>
                    <Accordion.Header>
                        <Text as="h3" variant="headline_6">
                            Budynek i otoczenie
                        </Text>
                    </Accordion.Header>
                    <Accordion.Content>
                        <ResponsiveTable>
                            <ResponsiveTableRow
                                title="Miejsca postojowe"
                                text={
                                    <PropertyDetailDetailsParkingInquiryButton
                                        vendorId={property.offer.vendor.id}
                                        offerId={property.offer.id}
                                        propertyId={property.id}
                                        vendorName={property.offer.vendor.name}
                                        offerName={property.offer.name}
                                    />
                                }
                            />
                            {!!property.garages && <ResponsiveTableRow title="Garaże" text={`garaż ${getGarageStandString(property.garages)}`} />}
                            {property.offer.type === OfferType.FLAT && property.offer.storage ? (
                                <ResponsiveTableRow
                                    title="Komórki lokatorskie"
                                    text={
                                        <Fragment>
                                            dostępne{property.offer.storage.quantity ? ` (${property.offer.storage.quantity})` : ""}
                                            {property.offer.storage.description ? `: ${property.offer.storage.description}` : ""}
                                        </Fragment>
                                    }
                                />
                            ) : null}
                            {standardDescription}
                            <ResponsiveTableRow title="Realizacja inwestycji" text={constructionEndDateText} />
                            <ResponsiveTableRow title="Udogodnienia" text={offerFacilities} />
                            <ResponsiveTableRow title="Przyroda" text={offerNaturalSites} />
                            <ResponsiveTableRow title="Charakterystyka energetyczna nieruchomości">
                                <PropertyDetailEnergyPerformance property={property} />
                            </ResponsiveTableRow>
                        </ResponsiveTable>
                    </Accordion.Content>
                </Accordion>

                <Accordion>
                    <Accordion.Header>
                        <Text as="h3" variant="headline_6">
                            Rezerwacja i finanse
                        </Text>
                    </Accordion.Header>
                    <Accordion.Content>
                        <ResponsiveTable>
                            <ResponsiveTableRow title="Rodzaj własności" text={foundOwnershipForm ? `${foundOwnershipForm.label}` : INFO_FROM_VENDOR} />
                            <ResponsiveTableRow title="Wysokość czynszu" text={rentPrice} />
                            <ResponsiveTableRow
                                title="Warunki rezerwacji"
                                text={property.offer.reservation_description ? property.offer.reservation_description : INFO_FROM_VENDOR}
                            />
                            <ResponsiveTableRow
                                title="Zabezpieczenie środków"
                                text={foundFundProtectionTypes ? `${foundFundProtectionTypes.label}` : INFO_FROM_VENDOR}
                            />
                        </ResponsiveTable>
                    </Accordion.Content>
                </Accordion>
            </div>
        </>
    );
};

const accordions = css({
    display: "flex",
    flexDirection: "column",
    rowGap: "1.6rem"
});
